<template>
  <Transition name="confirmation-fade">
    <div 
      v-if="isModalVisible" 
      class="confirmation-modal-overlay" 
      @click="handleOverlayClick"
    >
      <div class="confirmation-modal" @click.stop>
        <!-- Icon Section -->
        <div class="confirmation-modal__icon" :class="iconClass">
          <svg v-if="modalConfig.type === 'danger'" viewBox="0 0 24 24" fill="none">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else-if="modalConfig.type === 'warning'" viewBox="0 0 24 24" fill="none">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else-if="modalConfig.type === 'success'" viewBox="0 0 24 24" fill="none">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none">
            <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>

        <div class="confirmation-modal__content">
          <h3 class="confirmation-modal__title">{{ modalConfig.title }}</h3>
          <p v-if="modalConfig.message" class="confirmation-modal__message" v-html="modalConfig.message"></p>
        </div>
        
        <div class="confirmation-modal__actions">
          <button 
            class="confirmation-modal__button confirmation-modal__button--cancel"
            @click="onCancel"
          >
            {{ modalConfig.cancelText }}
          </button>
          <button 
            class="confirmation-modal__button"
            :class="confirmButtonClass"
            @click="onConfirm"
          >
            {{ modalConfig.confirmText }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { computed, watch } from 'vue'
import { useConfirmation } from './useConfirmation'

const { isModalVisible, modalConfig, handleConfirm, handleCancel } = useConfirmation()

const iconClass = computed(() => {
  return `confirmation-modal__icon--${modalConfig.value.type || 'info'}`
})

const confirmButtonClass = computed(() => {
  const type = modalConfig.value.type || 'info'
  if (type === 'danger') return 'confirmation-modal__button--danger'
  if (type === 'warning') return 'confirmation-modal__button--warning'
  if (type === 'success') return 'confirmation-modal__button--success'
  return 'confirmation-modal__button--confirm'
})

const handleOverlayClick = () => {
  if (modalConfig.value.allowOutsideClick) {
    onCancel()
  }
}

const onConfirm = () => {
  handleConfirm()
}

const onCancel = () => {
  handleCancel()
}

const handleEscapeKey = (event) => {
  if (event.key === 'Escape' && isModalVisible.value) {
    onCancel()
  }
}

// Watch for modal visibility to manage body scroll and escape key
watch(isModalVisible, (visible) => {
  if (visible) {
    document.addEventListener('keydown', handleEscapeKey)
    document.body.style.overflow = 'hidden'
  } else {
    document.removeEventListener('keydown', handleEscapeKey)
    document.body.style.overflow = ''
  }
})
</script>

<script>
export default {
  name: 'GlobalConfirmationModal'
}
</script>

<style scoped>
.confirmation-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
  padding: 24px;
}

.confirmation-modal {
  position: relative;
  max-width: 400px;
  width: 100%;
  padding: 32px;
  border-radius: 16px;
  text-align: center;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

/* Icon Styles */
.confirmation-modal__icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirmation-modal__icon svg {
  width: 32px;
  height: 32px;
}

.confirmation-modal__icon--info {
  background: #e3f2fd;
  border: 1px solid #90caf9;
  color: #1976d2;
}

.confirmation-modal__icon--danger {
  background: #fff5f5;
  border: 1px solid #ffcccc;
  color: #dc3545;
}

.confirmation-modal__icon--warning {
  background: #fff8e1;
  border: 1px solid #ffecb3;
  color: #f59e0b;
}

.confirmation-modal__icon--success {
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  color: #10b981;
}

.confirmation-modal__content {
  margin-bottom: 24px;
}

.confirmation-modal__title {
  margin: 0 0 12px 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  color: #000000;
}

.confirmation-modal__message {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #666666;
}

.confirmation-modal__actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.confirmation-modal__button {
  padding: 10px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;
  border: none;
}

.confirmation-modal__button:hover {
  transform: translateY(-1px);
}

.confirmation-modal__button--cancel {
  background: transparent;
  border: 1px solid #e0e0e0;
  color: #666666;
}

.confirmation-modal__button--cancel:hover {
  background: #f5f5f5;
  border-color: #000000;
  color: #000000;
}

.confirmation-modal__button--confirm {
  background: #1976d2;
  color: #ffffff;
}

.confirmation-modal__button--confirm:hover {
  background: #1565c0;
}

.confirmation-modal__button--danger {
  background: #dc3545;
  color: #ffffff;
}

.confirmation-modal__button--danger:hover {
  background: #c82333;
}

.confirmation-modal__button--warning {
  background: #f59e0b;
  color: #ffffff;
}

.confirmation-modal__button--warning:hover {
  background: #d97706;
}

.confirmation-modal__button--success {
  background: #10b981;
  color: #ffffff;
}

.confirmation-modal__button--success:hover {
  background: #059669;
}

/* Animations */
.confirmation-fade-enter-active,
.confirmation-fade-leave-active {
  transition: opacity 0.2s ease;
}

.confirmation-fade-enter-from,
.confirmation-fade-leave-to {
  opacity: 0;
}

.confirmation-fade-enter-active .confirmation-modal {
  animation: modalSlideIn 0.3s ease;
}

.confirmation-fade-leave-active .confirmation-modal {
  animation: modalSlideOut 0.2s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes modalSlideOut {
  from {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  to {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
}

@media (max-width: 640px) {
  .confirmation-modal {
    margin: 20px;
    padding: 24px;
  }
  
  .confirmation-modal__actions {
    flex-direction: column;
  }
  
  .confirmation-modal__button {
    width: 100%;
  }
}
</style>
